import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
} from '@mui/material';
import { Save, Refresh, Info } from '@mui/icons-material';
import axios from 'axios';
import UseToast from '../UseToast';

const GlobalAvatarSettings = ({ userId, buttonColor = '#000000' }) => {
  const { showErrorToast, showSuccessToast } = UseToast();

  const [loading, setLoading] = useState(false);
  const [assignedHeygenKeys, setAssignedHeygenKeys] = useState([]);
  const [assignedAvatarKeys, setAssignedAvatarKeys] = useState([]);
  const [assignedVoiceKeys, setAssignedVoiceKeys] = useState([]);

  const [settings, setSettings] = useState({
    enabled: true,
    heygen_api_key_id: '',
    heygen_key_id: '',
    avatar_api_key_id: '',
    avatar_key_id: '',
    voice_api_key_id: '',
    voice_key_id: '',
    defaultResolution: '720p',
  });

  const [selectedHeygenApiKey, setSelectedHeygenApiKey] = useState('');
  const [customHeygenKey, setCustomHeygenKey] = useState('');
  const [selectedAvatarApiKey, setSelectedAvatarApiKey] = useState('');
  const [customAvatarKey, setCustomAvatarKey] = useState('');
  const [selectedVoiceApiKey, setSelectedVoiceApiKey] = useState('');
  const [customVoiceKey, setCustomVoiceKey] = useState('');

  // Fetch assigned keys
  useEffect(() => {
    const fetchKeys = async () => {
      try {
        const response = await axios.get(`/api/user-api-keys/form-keys/${userId}`);
        if (response.data) {
          const heygenKeys = response.data.assignedKeys?.heygen || [];
          const avatarKeys = response.data.assignedKeys?.avatar || [];
          const voiceKeys = response.data.assignedKeys?.voice || [];
          setAssignedHeygenKeys(heygenKeys);
          setAssignedAvatarKeys(avatarKeys);
          setAssignedVoiceKeys(voiceKeys);
        }
      } catch (error) {
        console.error('Error fetching keys:', error);
        showErrorToast('Failed to fetch API keys');
      }
    };

    if (userId) {
      fetchKeys();
    }
  }, [userId]);

  // Fetch global avatar settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/wayfinder/global-avatar-settings/${userId}`);

        if (response.data.success) {
          const data = response.data.data;
          setSettings(data);

          // Initialize HeyGen key selection
          // Check if there's a MongoDB ID - if yes, it's from dropdown; if no, it's custom
          if (data.heygen_key_id) {
            if (data.heygen_api_key_id) {
              // Has MongoDB ID = selected from dropdown
              setSelectedHeygenApiKey(data.heygen_key_id);
              setCustomHeygenKey('');
            } else {
              // No MongoDB ID = custom key (even if it matches an assigned key)
              setSelectedHeygenApiKey('');
              setCustomHeygenKey(data.heygen_key_id);
            }
          } else {
            setSelectedHeygenApiKey('');
            setCustomHeygenKey('');
          }

          // Initialize Avatar key selection
          if (data.avatar_key_id) {
            if (data.avatar_api_key_id) {
              // Has MongoDB ID = selected from dropdown
              setSelectedAvatarApiKey(data.avatar_key_id);
              setCustomAvatarKey('');
            } else {
              // No MongoDB ID = custom key
              setSelectedAvatarApiKey('');
              setCustomAvatarKey(data.avatar_key_id);
            }
          } else {
            setSelectedAvatarApiKey('');
            setCustomAvatarKey('');
          }

          // Initialize Voice key selection
          if (data.voice_key_id) {
            if (data.voice_api_key_id) {
              // Has MongoDB ID = selected from dropdown
              setSelectedVoiceApiKey(data.voice_key_id);
              setCustomVoiceKey('');
            } else {
              // No MongoDB ID = custom key
              setSelectedVoiceApiKey('');
              setCustomVoiceKey(data.voice_key_id);
            }
          } else {
            setSelectedVoiceApiKey('');
            setCustomVoiceKey('');
          }
        }
      } catch (error) {
        console.error('Error fetching global avatar settings:', error);
        showErrorToast('Failed to fetch global avatar settings');
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchSettings();
    }
  }, [userId, assignedHeygenKeys, assignedAvatarKeys, assignedVoiceKeys]);

  // Handle HeyGen key selection
  const handleHeygenKeyChange = event => {
    const apiKey = event.target.value;
    setSelectedHeygenApiKey(apiKey);

    // Find the selected key to get its MongoDB ID
    const selectedKey = assignedHeygenKeys.find(k => k.api_key === apiKey);

    setSettings(prev => ({
      ...prev,
      heygen_key_id: apiKey,
      heygen_api_key_id: selectedKey ? selectedKey._id : '', // Store MongoDB ID if from dropdown
    }));

    if (apiKey) {
      setCustomHeygenKey('');
    }
  };

  // Handle custom HeyGen key input
  const handleCustomHeygenKeyChange = event => {
    const customKey = event.target.value;
    setCustomHeygenKey(customKey);
    
    if (customKey) {
      setSelectedHeygenApiKey('');
      setSettings(prev => ({
        ...prev,
        heygen_key_id: customKey,
        heygen_api_key_id: '', // Clear MongoDB ID for custom key
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        heygen_key_id: '',
        heygen_api_key_id: '',
      }));
    }
  };

  // Handle Avatar key selection
  const handleAvatarKeyChange = event => {
    const apiKey = event.target.value;
    setSelectedAvatarApiKey(apiKey);

    // Find the selected key to get its MongoDB ID
    const selectedKey = assignedAvatarKeys.find(k => k.api_key === apiKey);

    setSettings(prev => ({
      ...prev,
      avatar_key_id: apiKey,
      avatar_api_key_id: selectedKey ? selectedKey._id : '', // Store MongoDB ID if from dropdown
    }));

    if (apiKey) {
      setCustomAvatarKey('');
    }
  };

  // Handle custom Avatar key input
  const handleCustomAvatarKeyChange = event => {
    const customKey = event.target.value;
    setCustomAvatarKey(customKey);
    
    if (customKey) {
      setSelectedAvatarApiKey('');
      setSettings(prev => ({
        ...prev,
        avatar_key_id: customKey,
        avatar_api_key_id: '', // Clear MongoDB ID for custom key
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        avatar_key_id: '',
        avatar_api_key_id: '',
      }));
    }
  };

  // Handle Voice key selection
  const handleVoiceKeyChange = event => {
    const apiKey = event.target.value;
    setSelectedVoiceApiKey(apiKey);

    // Find the selected key to get its MongoDB ID
    const selectedKey = assignedVoiceKeys.find(k => k.api_key === apiKey);

    setSettings(prev => ({
      ...prev,
      voice_key_id: apiKey,
      voice_api_key_id: selectedKey ? selectedKey._id : '', // Store MongoDB ID if from dropdown
    }));

    if (apiKey) {
      setCustomVoiceKey('');
    }
  };

  // Handle custom Voice key input
  const handleCustomVoiceKeyChange = event => {
    const customKey = event.target.value;
    setCustomVoiceKey(customKey);
    
    if (customKey) {
      setSelectedVoiceApiKey('');
      setSettings(prev => ({
        ...prev,
        voice_key_id: customKey,
        voice_api_key_id: '', // Clear MongoDB ID for custom key
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        voice_key_id: '',
        voice_api_key_id: '',
      }));
    }
  };

  // Handle resolution change
  const handleResolutionChange = event => {
    setSettings(prev => ({ ...prev, defaultResolution: event.target.value }));
  };

  // Handle enabled toggle
  // const handleEnabledToggle = event => {
  //   setSettings(prev => ({ ...prev, enabled: event.target.checked }));
  // };

  // Save settings
  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await axios.put(`/api/wayfinder/global-avatar-settings/${userId}`, settings);

      if (response.data.success) {
        showSuccessToast('Global avatar settings saved successfully');
        
        // Refresh settings to ensure UI is in sync with saved data
        const fetchResponse = await axios.get(`/api/wayfinder/global-avatar-settings/${userId}`);
        if (fetchResponse.data.success) {
          const data = fetchResponse.data.data;
          setSettings(data);

          // Re-initialize HeyGen key selection
          // Check if there's a MongoDB ID - if yes, it's from dropdown; if no, it's custom
          if (data.heygen_key_id) {
            if (data.heygen_api_key_id) {
              // Has MongoDB ID = selected from dropdown
              setSelectedHeygenApiKey(data.heygen_key_id);
              setCustomHeygenKey('');
            } else {
              // No MongoDB ID = custom key (even if it matches an assigned key)
              setSelectedHeygenApiKey('');
              setCustomHeygenKey(data.heygen_key_id);
            }
          } else {
            setSelectedHeygenApiKey('');
            setCustomHeygenKey('');
          }

          // Re-initialize Avatar key selection
          if (data.avatar_key_id) {
            if (data.avatar_api_key_id) {
              // Has MongoDB ID = selected from dropdown
              setSelectedAvatarApiKey(data.avatar_key_id);
              setCustomAvatarKey('');
            } else {
              // No MongoDB ID = custom key
              setSelectedAvatarApiKey('');
              setCustomAvatarKey(data.avatar_key_id);
            }
          } else {
            setSelectedAvatarApiKey('');
            setCustomAvatarKey('');
          }

          // Re-initialize Voice key selection
          if (data.voice_key_id) {
            if (data.voice_api_key_id) {
              // Has MongoDB ID = selected from dropdown
              setSelectedVoiceApiKey(data.voice_key_id);
              setCustomVoiceKey('');
            } else {
              // No MongoDB ID = custom key
              setSelectedVoiceApiKey('');
              setCustomVoiceKey(data.voice_key_id);
            }
          } else {
            setSelectedVoiceApiKey('');
            setCustomVoiceKey('');
          }
        }
      }
    } catch (error) {
      console.error('Error saving global avatar settings:', error);
      showErrorToast(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  // Reset settings
  const handleReset = async () => {
    try {
      setLoading(true);
      const response = await axios.delete(`/api/wayfinder/global-avatar-settings/${userId}`);

      if (response.data.success) {
        setSettings(response.data.data);
        setSelectedHeygenApiKey('');
        setCustomHeygenKey('');
        setSelectedAvatarApiKey('');
        setCustomAvatarKey('');
        setSelectedVoiceApiKey('');
        setCustomVoiceKey('');
        showSuccessToast('Settings reset to defaults');
      }
    } catch (error) {
      console.error('Error resetting settings:', error);
      showErrorToast('Failed to reset settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mx: 'auto', mt: 3 }}>
      <Card>
        <CardContent>
          {/* <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography
              variant="h5"
              sx={{
                color: buttonColor,
                fontWeight: 600,
              }}
            >
              Global Avatar Settings
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  onChange={handleEnabledToggle}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: buttonColor,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: buttonColor,
                    },
                  }}
                />
              }
              label="Enable Global Settings"
            />
          </Box> */}

          {/* <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
            These settings will be used as defaults for all locations unless overridden at the
            location level.
          </Alert> */}

          <Grid container spacing={3}>
            {/* HeyGen API Key */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: buttonColor,
                      borderBottom: `2px solid ${buttonColor}`,
                      pb: 1,
                      mb: 2,
                    }}
                  >
                    HeyGen API Settings
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Select Assigned HeyGen Key
                    </Typography>
                    <Select
                      value={selectedHeygenApiKey}
                      onChange={handleHeygenKeyChange}
                      displayEmpty
                      disabled={!settings.enabled}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {assignedHeygenKeys.map(key => (
                        <MenuItem key={key._id} value={key.api_key}>
                          {key.name || key.api_key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Or Enter Custom HeyGen Key"
                    value={customHeygenKey}
                    onChange={handleCustomHeygenKeyChange}
                    placeholder="Enter custom HeyGen API key"
                    variant="outlined"
                    disabled={!settings.enabled}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Avatar Key */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: buttonColor,
                      borderBottom: `2px solid ${buttonColor}`,
                      pb: 1,
                      mb: 2,
                    }}
                  >
                    Avatar Settings
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Select Assigned Avatar Key
                    </Typography>
                    <Select
                      value={selectedAvatarApiKey}
                      onChange={handleAvatarKeyChange}
                      displayEmpty
                      disabled={!settings.enabled}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {assignedAvatarKeys.map(key => (
                        <MenuItem key={key._id} value={key.api_key}>
                          {key.name || key.api_key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Or Enter Custom Avatar Key"
                    value={customAvatarKey}
                    onChange={handleCustomAvatarKeyChange}
                    placeholder="Enter custom avatar key"
                    variant="outlined"
                    disabled={!settings.enabled}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Voice Key */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: buttonColor,
                      borderBottom: `2px solid ${buttonColor}`,
                      pb: 1,
                      mb: 2,
                    }}
                  >
                    Voice Settings
                  </Typography>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Select Assigned Voice Key
                    </Typography>
                    <Select
                      value={selectedVoiceApiKey}
                      onChange={handleVoiceKeyChange}
                      displayEmpty
                      disabled={!settings.enabled}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {assignedVoiceKeys.map(key => (
                        <MenuItem key={key._id} value={key.api_key}>
                          {key.name || key.api_key}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Or Enter Custom Voice Key"
                    value={customVoiceKey}
                    onChange={handleCustomVoiceKeyChange}
                    placeholder="Enter custom voice key"
                    variant="outlined"
                    disabled={!settings.enabled}
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Default Resolution */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: buttonColor,
                      borderBottom: `2px solid ${buttonColor}`,
                      pb: 1,
                      mb: 2,
                    }}
                  >
                    Video Settings
                  </Typography>

                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Default Resolution
                    </Typography>
                    <Select
                      value={settings.defaultResolution}
                      onChange={handleResolutionChange}
                      disabled={!settings.enabled}
                    >
                      <MenuItem value="4k">4K Ultra HD (3840x2160)</MenuItem>
                      <MenuItem value="1080p">Full HD (1920x1080)</MenuItem>
                      <MenuItem value="720p">HD (1280x720)</MenuItem>
                      <MenuItem value="480p">SD (854x480)</MenuItem>
                    </Select>
                  </FormControl>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleReset}
              disabled={loading}
              sx={{
                borderColor: buttonColor,
                color: buttonColor,
                '&:hover': {
                  borderColor: buttonColor,
                  backgroundColor: `${buttonColor}10`,
                },
              }}
            >
              Reset to Defaults
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={loading}
              sx={{
                backgroundColor: buttonColor,
                '&:hover': {
                  backgroundColor: buttonColor,
                  opacity: 0.9,
                },
              }}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GlobalAvatarSettings;
